import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CameraIcon, ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { WebsiteGalleryImage } from '../../data/website';
interface RoadGalleryProps {
  eyebrow: string;
  title: string;
  images: WebsiteGalleryImage[];
}
const easeOut = [0.23, 1, 0.32, 1] as const;
export function RoadGallery({
  eyebrow,
  title,
  images
}: RoadGalleryProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const trackRef = useRef<HTMLUListElement>(null);
  const count = images.length;
  const close = useCallback(() => setOpenIndex(null), []);
  const step = useCallback((direction: 1 | -1) => setOpenIndex(current => current === null ? current : (current + direction + count) % count), [count]);
  const syncEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setAtStart(track.scrollLeft <= 8);
    setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 8);
  }, []);
  useEffect(() => {
    syncEdges();
  }, [syncEdges, count]);
  const scrollByPage = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({
      left: direction * track.clientWidth * 0.8,
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    if (openIndex === null) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openIndex, close, step]);
  if (!count) return null;
  const active = openIndex === null ? null : images[openIndex];
  return <section id="photos" className="scroll-mt-4 border-t border-white/[0.08] bg-[#17110e] py-14 sm:py-20" data-id="element-1165" data-mp="2y2m0ccjfpqnw">
      <div className="mx-auto mb-9 max-w-5xl px-5 text-center sm:px-8" data-id="element-1166" data-mp="57vtycjymgy6">
        <div className="mb-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#e8a369]" data-id="element-1167" data-mp="8j0h8yl2bmm">
          <CameraIcon className="h-3.5 w-3.5" data-id="element-1168" data-mp="97mhm7e6wgyh" /> {eyebrow}
        </div>
        <h2 className="font-serif-display text-[43px] leading-none text-white sm:text-[56px]" data-id="element-1169" data-mp="96bco490d35s">{title}</h2>
      </div>

      <div className="relative" data-id="element-1170" data-mp="o2k9z09c96fk">
        {/* Fixed row height with automatic width: portrait and landscape shots
            both appear whole, and the row stays one clean line. */}
        <ul ref={trackRef} onScroll={syncEdges} className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-5 pb-3 sm:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-1171" data-mp="wwfeq76jsuuo">
          {images.map((image, index) => <li key={image.id} className="shrink-0 snap-start" data-id="element-1172" data-mp="11xmmw7clrw1v" data-mpi={index}>
              <button type="button" onClick={() => setOpenIndex(index)} aria-label={`View photo: ${image.caption || 'Untitled'}`} className="group relative block h-[260px] overflow-hidden rounded-sm bg-[#241711] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e8a369] sm:h-[360px] lg:h-[440px]" data-id="element-1173" data-mp="1lhk7oinautdq">
                <img src={image.url} alt={image.caption || `${title} photo ${index + 1}`} loading="lazy" className="block h-full w-auto max-w-none transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-1174" data-mp="12r2gwjm127v0" />
                <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,transparent_55%,rgba(16,13,11,0.85))] opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100" data-id="element-1175" data-mp="xrzn5vbhzo2c" />
                {image.caption && <span className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-1 p-3 text-left text-[12px] font-medium text-[#f3dec9] opacity-0 transition-[opacity,transform] duration-200 ease-out group-hover:translate-y-0 group-hover:opacity-100" data-id="element-1176" data-mp="ntjxzxw16ovh">
                    {image.caption}
                  </span>}
              </button>
            </li>)}
        </ul>

        {count > 1 && <>
            <button type="button" onClick={() => scrollByPage(-1)} disabled={atStart} aria-label="Scroll photos left" className="absolute left-3 top-1/2 hidden -translate-y-1/2 rounded-full border border-white/15 bg-black/55 p-2.5 text-white backdrop-blur transition-opacity duration-150 ease-out hover:bg-black/75 disabled:pointer-events-none disabled:opacity-0 sm:block" data-id="element-1177" data-mp="3u6ks27nj53lu">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-1178" data-mp="1ieebi0phdsmz" />
            </button>
            <button type="button" onClick={() => scrollByPage(1)} disabled={atEnd} aria-label="Scroll photos right" className="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-full border border-white/15 bg-black/55 p-2.5 text-white backdrop-blur transition-opacity duration-150 ease-out hover:bg-black/75 disabled:pointer-events-none disabled:opacity-0 sm:block" data-id="element-1179" data-mp="1dhxe0fwe61x8">
              <ChevronRightIcon className="h-5 w-5" data-id="element-1180" data-mp="12ztxap9o89z5" />
            </button>
          </>}
      </div>

      <p className="mt-4 text-center text-[11.5px] text-zinc-600" data-id="element-1181" data-mp="170m4ze2wa6tb">
        {count} photo{count === 1 ? '' : 's'} · swipe or use the arrows, tap any photo to open it
      </p>

      <AnimatePresence data-id="element-1182" data-mp="1gzbmv6usjr9k">
        {active && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8" data-id="element-1183" data-mp="1h04mzoz8viiq">
            <motion.button type="button" aria-label="Close photo" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} onClick={close} className="absolute inset-0 bg-black/92" data-id="element-1184" data-mp="2necbsslef96n" />
            <motion.figure key={active.id} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.24,
          ease: easeOut
        }} role="dialog" aria-modal="true" aria-label={active.caption || 'Photo from the road'} className="relative z-10 flex max-h-full w-full max-w-4xl flex-col items-center" data-id="element-1185" data-mp="2co9108m12kbp">
              <img src={active.url} alt={active.caption || 'Photo from the road'} className="max-h-[80vh] w-auto max-w-full rounded-sm object-contain" data-id="element-1186" data-mp="1oz7vat4kxna4" />
              <figcaption className="mt-4 flex items-center gap-4 text-[12.5px] text-[#e7d5c7]/80" data-id="element-1187" data-mp="wb2bc8lco2bc">
                <span data-id="element-1188" data-mp="37xcik63wrjeo">{active.caption}</span>
                <span className="text-zinc-600" data-id="element-1189" data-mp="37vjcarx7j177">
                  {(openIndex ?? 0) + 1} / {count}
                </span>
              </figcaption>
            </motion.figure>

            <button type="button" onClick={close} aria-label="Close" className="absolute right-4 top-4 z-10 rounded-full border border-white/15 bg-black/40 p-2 text-white transition-colors hover:bg-white/10 sm:right-8 sm:top-8" data-id="element-1190" data-mp="2y117fjg20zlt">
              <XIcon className="h-5 w-5" data-id="element-1191" data-mp="3vmord4vbppgg" />
            </button>

            {count > 1 && <>
                <button type="button" onClick={() => step(-1)} aria-label="Previous photo" className="absolute left-3 z-10 rounded-full border border-white/15 bg-black/40 p-2.5 text-white transition-colors hover:bg-white/10 sm:left-6" data-id="element-1192" data-mp="3hn5xnh39egno">
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-1193" data-mp="31xf9kg8uru6j" />
                </button>
                <button type="button" onClick={() => step(1)} aria-label="Next photo" className="absolute right-3 z-10 rounded-full border border-white/15 bg-black/40 p-2.5 text-white transition-colors hover:bg-white/10 sm:right-6" data-id="element-1194" data-mp="31vcbra1gkwr9">
                  <ChevronRightIcon className="h-5 w-5" data-id="element-1195" data-mp="3knwx86etvzl1" />
                </button>
              </>}
          </div>}
      </AnimatePresence>
    </section>;
}